import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { LoadingBlock } from "../design-system/components/desktop";

const ProtectedRoute = ({
  children,
  requireSeller = false,
  requireAdmin = false,
  requireBuyer = false,
}) => {
  const { user, isAuthenticated, loading, initialized } = useAuth();
  const location = useLocation();

  // While the session is still being restored, say what is happening rather
  // than showing a bare ring. A spinner with no words reads as a hang, and
  // under reduced motion it does not even turn.
  if (!initialized) {
    return <LoadingBlock>Checking your session</LoadingBlock>;
  }

  // The three console.log calls that used to sit here printed the whole `user`
  // object on every render of every protected route: name, email, role, id.
  // Browser console output is readable by anything with devtools and is
  // captured by some session-replay tools, so that was a privacy leak on the
  // most-rendered component in the app. Removed rather than gated.

  // If not authenticated, redirect based on the route being accessed
  if (!isAuthenticated) {
    // If trying to access admin routes, redirect to admin login
    if (location.pathname.startsWith("/admin")) {
      return <Navigate to="/admin-login" state={{ from: location }} replace />;
    }
    // For all other routes, redirect to regular login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  const userRole = user?.role;

  // Admin access required
  if (requireAdmin && userRole !== "admin") {
    // If user is not admin but trying to access admin routes
    if (userRole === "seller") {
      return <Navigate to="/dashboard" replace />;
    } else if (userRole === "buyer") {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  // Seller access required
  if (requireSeller && userRole !== "seller") {
    if (userRole === "admin") {
      return <Navigate to="/admin-dashboard" replace />;
    } else if (userRole === "buyer") {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  // User access required (regular user, not seller or admin)
  if (requireBuyer && userRole !== "buyer") {
    if (userRole === "admin") {
      return <Navigate to="/admin-dashboard" replace />;
    } else if (userRole === "seller") {
      return <Navigate to="/dashboard" replace />;
    } else {
      return <Navigate to="/login" replace />;
    }
  }

  // If user is authenticated and has correct role, render children
  return children;
};

export default ProtectedRoute;
